import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { ArrowRight, FileText, Car, Truck, GaugeCircle, Map, Star, Zap, Shield, Rocket, Navigation } from 'lucide-react';
import { motion } from 'framer-motion';
import { FaRupeeSign, FaClock, FaChartLine, FaPercentage, FaWallet, FaBalanceScale, FaCheckCircle, FaExchangeAlt } from "react-icons/fa";
import { BiCar } from "react-icons/bi";
import FAQSchema from "../../FAQSchema";
import AdBanner from "../AdPage/AdBanner";

function Icon({ id, open }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
      />
    </svg>
  );
}

const TataMotorsCarLoan = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [loanData, setLoanData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const faqs = [
    {
      question: "What is the minimum income needed for a Tata car loan?",
      answer: "A monthly income of ₹20,000–₹25,000 is generally required.",
    },
    {
      question: "Can I get 100% financing on a Tata car?",
      answer: "Yes, some lenders offer full on-road price financing depending on your credit profile.",
    },
    {
      question: "Are car loans available for Tata electric vehicles?",
      answer: "Absolutely. All Tata EVs including Nexon EV and Tiago EV are eligible for loans.",
    },
    {
      question: "Which Tata car is the cheapest to finance?",
      answer: "Tata Tiago and Punch are budget-friendly with lower EMIs.",
    },
    {
      question: "Can I repay my Tata car loan early?",
      answer: "Yes, most banks allow foreclosure or part pre-payment after a lock-in period.",
    },
    {
      question: "What interest rate can I expect for a Tata car loan?",
      answer: "It varies between 8.5% to 14.5% based on credit score and income.",
    },
    {
      question: "Is a co-applicant allowed in Tata car loan applications?",
      answer: "Yes, adding a co-applicant may increase eligibility and loan amount.",
    },
    {
      question: "How soon is the car loan disbursed after approval?",
      answer: "Usually within 1–2 business days of verification and document signing.",
    },
    {
      question: "Can I apply for a Tata car loan online?",
      answer: "Yes, both banks and platforms like TheFinMaster allow digital applications.",
    },
    {
      question: "Is loan insurance compulsory?",
      answer: "It’s optional but advisable to safeguard against unforeseen situations.",
    },
    {
      question: "What is the typical loan tenure?",
      answer: "Car loans are available for 1 to 7 years, depending on the lender.",
    },
    {
      question: "What credit score is needed to get approved?",
      answer: "A score of 700 or above is ideal for easy approval and better rates.",
    },
    {
      question: "Are tax benefits available on car loans?",
      answer: "Only if the car is used for business or professional purposes.",
    },
    {
      question: "Are zero down payment options available for Tata cars?",
      answer: "Yes, with select banks and customer profiles.",
    },
    {
      question: "Can NRIs apply for Tata Motors car loans?",
      answer: "Yes, through NRI-specific schemes and with an Indian co-applicant.",
    },
    {
      question: "What happens if I miss an EMI?",
      answer: "Late EMIs may attract penalties and impact your CIBIL score.",
    },
    {
      question: "Do lenders offer EMI-free periods?",
      answer: "Some lenders offer EMI holiday schemes at the beginning of the tenure.",
    },
    {
      question: "Can I refinance my existing Tata car loan?",
      answer: "Yes, refinancing helps reduce interest burden if rates are lower now.",
    },
    {
      question: "How can I increase my loan eligibility?",
      answer: "Maintain a strong credit score, stable income, and low existing debt.",
    },
    {
      question: "Where can I compare Tata car loan options online?",
      answer: "Use TheFinMaster to compare loan offers and apply within minutes.",
    },
  ];

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  const features = [
    { icon: '🛡️', text: 'Reliable & Accurate Estimates' },
    { icon: '⏱️', text: 'Takes Less Than a Minute' },
    { icon: '📉', text: 'Instantly Shows EMI Breakup' },
    { icon: '📄', text: 'No Paperwork or Documentation' },
    { icon: '📲', text: 'Easy to Use on Any Device' },
    { icon: '💰', text: 'Helps You Save on Loans' },
  ];

  const benefits = [
    {
      title: "Tata Tiago",
      desc: "Affordable hatchback with premium features.",
      icon: <Car className="w-7 h-7 text-red-500" />,
      gradient: "from-red-50 to-red-100",
    },
    {
      title: "Tata Punch",
      desc: "Stylish and rugged micro SUV.",
      icon: <Truck className="w-7 h-7 text-indigo-500" />,
      gradient: "from-indigo-50 to-indigo-100",
    },
    {
      title: "Tata Tigor",
      desc: "Compact sedan with excellent mileage.",
      icon: <GaugeCircle className="w-7 h-7 text-cyan-500" />,
      gradient: "from-cyan-50 to-cyan-100",
    },
    {
      title: "Tata Altroz",
      desc: "Premium hatchback with 5-star safety rating.",
      icon: <Map className="w-7 h-7 text-green-500" />,
      gradient: "from-green-50 to-green-100",
    },
    {
      title: "Tata Nexon",
      desc: "Compact SUV with petrol, diesel, and EV variants.",
      icon: <Star className="w-7 h-7 text-yellow-500" />,
      gradient: "from-yellow-50 to-yellow-100",
    },
    {
      title: "Tata Harrier",
      desc: "Powerful mid-size SUV with commanding road presence.",
      icon: <Zap className="w-7 h-7 text-orange-500" />,
      gradient: "from-orange-50 to-orange-100",
    },
    {
      title: "Tata Safari",
      desc: "6/7-seater premium SUV.",
      icon: <Shield className="w-7 h-7 text-purple-500" />,
      gradient: "from-purple-50 to-purple-100",
    },
    {
      title: "Tata Nexon EV",
      desc: "One of India’s leading electric cars.",
      icon: <Rocket className="w-7 h-7 text-blue-500" />,
      gradient: "from-blue-50 to-blue-100",
    },
    {
      title: "Tata Tiago EV",
      desc: "Affordable electric city car.",
      icon: <Navigation className="w-7 h-7 text-teal-500" />,
      gradient: "from-teal-50 to-teal-100",
    },
  ];

  const highlights = [
    { icon: <FaRupeeSign className="text-3xl text-blue-700 mb-4" />, feature: "Loan Amount", details: "₹1 Lakh to ₹50 Lakh+" },
    { icon: <FaClock className="text-3xl text-blue-700 mb-4" />, feature: "Loan Tenure", details: "1 to 7 years" },
    { icon: <FaChartLine className="text-3xl text-blue-700 mb-4" />, feature: "Interest Rate Range", details: "8.5% to 14.5% per annum" },
    { icon: <FaPercentage className="text-3xl text-blue-700 mb-4" />, feature: "Processing Fees", details: "0.25% to 1.5% of loan amount" },
    { icon: <FaWallet className="text-3xl text-blue-700 mb-4" />, feature: "Minimum Down Payment", details: "10% to 25% of the on-road price" },
    { icon: <FaBalanceScale className="text-3xl text-blue-700 mb-4" />, feature: "Loan to Value (LTV)", details: "Up to 100% for eligible profiles" },
    { icon: <FaCheckCircle className="text-3xl text-blue-700 mb-4" />, feature: "Approval Timeline", details: "24–48 hours after documentation" },
    { icon: <FaExchangeAlt className="text-3xl text-blue-700 mb-4" />, feature: "EMI Flexibility", details: "Step-up EMIs, balloon payments, EMI holidays" },
  ];

  return (
    <>
      <Helmet>
        <title>Tata Motors Car Loan in India – Compare Rates & Apply Online</title>
        <meta
          name="description"
          content="Explore Tata Motors car loan options in India. Compare interest rates, EMI, and apply online for models like Tiago, Nexon, Harrier, and more."
        />
        <meta
          name="keywords"
          content="tata motors car loan, tata nexon loan, tata tiago loan, car loan emi calculator"
        />
         <link rel="canonical" href="https://thefinmaster.com/tata-motors-car-loan" />
        <meta
          name="robots"
          content="max-snippet:-1, max-image-preview:standard"
        />
        <meta name="robots" content="max-video-preview:-1" />
        {/* <link rel="canonical" href="https://thefinmaster.com/tata-motors-car-loan" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard, max-video-preview:-1" />
        <meta property="og:title" content="Tata Motors Car Loan in India" />
        <meta property="og:description" content="Compare and apply for Tata Motors car loans online with ease." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://thefinmaster.com/tata-motors-car-loan" />
        <meta name="twitter:card" content="summary_large_image" /> */}
      </Helmet>
      <FAQSchema faqs={faqs} />
      <div className="w-full flex justify-center items-center px-4 mt-20">
        <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
          <AdBanner displayOrder="1" />
        </div>
      </div>
      <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
        <ol className="list-none p-0 inline-flex">
          <li className="flex items-center">
            <Link to="/" className="text-blue-600 hover:text-blue-800">Home</Link>
            <svg
              className="w-4 h-4 mx-2 text-gray-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </li>
          <li className="flex items-center">
            <span className="text-gray-500">Tata Motors Car Loan</span>
          </li>
        </ol>
      </nav>
      <div className="w-full flex flex-col items-center">
        <div className="w-full flex justify-center">
          <div className="w-full sm:w-[95%] flex flex-col sm:flex-row justify-center items-center">
            <div className="w-full sm:w-[50%]">
              <div className="w-[90%]">
                <h1 className="text-3xl md:text-5xl font-bold mt-6 md:mt-0 md:pl-4 pl-6">
                  <span className="text-blue-600">Tata Motors Car Loan </span> 
                </h1>
                <p className="text-lg md:text-base lg:text-lg mt-4 pl-4">
                TATA Motors is a trusted Indian brand offering a wide range of vehicles, from budget-friendly hatchbacks to premium SUVs. Whether you are planning to buy a Tiago or a Harrier, the right car loan can make ownership smoother and more affordable. This guide walks you through everything you need to know about getting a car loan for a TATA Motors vehicle, from interest rates and eligibility to documentation, repayment tips, and FAQs.                </p>
              </div>
            </div>
            <div className="w-full sm:w-[50%]">
              <img
                className="w-full max-w-full mt-2"
                src="../assets/tatacarloan.jpeg"
                alt="Tata Motors Car"
              />
            </div>
          </div>
        </div>
      </div>
      <section className="bg-gradient-to-r from-blue-50 via-white to-blue-100 py-16 px-6 sm:px-12 lg:px-20 rounded-3xl shadow-lg max-w-7xl mx-auto mt-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800">
            Popular Tata Cars Eligible for Car Loans
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
            All major Tata car models are eligible for financing through banks, NBFCs, and digital lending platforms. Loans are available for both new Tata cars and certified pre-owned vehicles.
          </p>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item, index) => (
            <div
              key={index}
              className={`bg-gradient-to-br ${item.gradient} rounded-2xl p-5 shadow hover:shadow-md hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-center bg-white w-14 h-14 rounded-full shadow mb-4 mx-auto">
                {item.icon}
              </div>
              <h3 className="text-lg font-semibold text-slate-800 text-center mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 text-center leading-relaxed">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </section>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="2" />
        </div>
      </div>
      <section className="w-full bg-gradient-to-b from-white via-blue-50 to-white py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
        <h2 className="text-3xl font-bold text-slate-800 mb-4">
             Tata Motors Car Loan Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-4">
            {highlights.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden p-6 rounded-2xl shadow-xl bg-white/70 backdrop-blur-xl border border-gray-200 transition-transform transform hover:scale-105"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-gradient-to-br from-blue-300 via-pink-300 to-yellow-300 rounded-full opacity-30 blur-3xl group-hover:opacity-50 transition-all"></div>
                <div className="text-3xl text-blue-700 mb-4">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.feature}</h3>
                <p className="text-gray-700">{item.details}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Top Banks Offering Tata Car Loans
        </h2>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { lender: "HDFC Bank", rate: "8.50%", tenure: "7 years", features: "Instant approval for pre-approved customers" },
              { lender: "SBI", rate: "8.70%", tenure: "7 years", features: "Lower rates for women borrowers" },
              { lender: "Axis Bank", rate: "9.00%", tenure: "5 years", features: "Custom EMI plans and top-up loan facility" },
              { lender: "ICICI Bank", rate: "9.25%", tenure: "7 years", features: "High LTV, minimal paperwork" },
              { lender: "Kotak Mahindra", rate: "9.50%", tenure: "6 years", features: "Quick disbursal, flexible repayment" },
              { lender: "Tata Capital", rate: "10.00%", tenure: "5 years", features: "Exclusive offers for Tata car buyers" },
              { lender: "IDFC First Bank", rate: "9.75%", tenure: "6 years", features: "Digital process with instant decisioning" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.lender}</h3>
                <div className="text-gray-600 mb-1"><strong>Starting Interest Rate:</strong> {item.rate}</div>
                <div className="text-gray-600 mb-1"><strong>Max Tenure:</strong> {item.tenure}</div>
                <div className="text-gray-600"><strong>Special Features:</strong> {item.features}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Lender</th>
                  <th className="p-3 border border-gray-300 text-left">Starting Interest Rate</th>
                  <th className="p-3 border border-gray-300 text-left">Max Tenure</th>
                  <th className="p-3 border border-gray-300 text-left">Special Features</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["HDFC Bank", "8.50%", "7 years", "Instant approval for pre-approved customers"],
                  ["SBI", "8.70%", "7 years", "Lower rates for women borrowers"],
                  ["Axis Bank", "9.00%", "5 years", "Custom EMI plans and top-up loan facility"],
                  ["ICICI Bank", "9.25%", "7 years", "High LTV, minimal paperwork"],
                  ["Kotak Mahindra", "9.50%", "6 years", "Quick disbursal, flexible repayment"],
                  ["Tata Capital", "10.00%", "5 years", "Exclusive offers for Tata car buyers"],
                  ["IDFC First Bank", "9.75%", "6 years", "Digital process with instant decisioning"],
                ].map(([lender, rate, tenure, features], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{lender}</td>
                    <td className="p-3 border border-gray-300">{rate}</td>
                    <td className="p-3 border border-gray-300">{tenure}</td>
                    <td className="p-3 border border-gray-300">{features}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <section className="bg-blue-50 py-6 px-4 sm:px-6 md:px-20 lg:px-20 rounded-3xl shadow-lg max-w-6xl mx-auto mt-10">
        <div className="flex flex-col md:flex-row justify-between items-center gap-10">
          <div className="max-w-xl space-y-6 text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-blue-900 leading-tight">
              Calculate Your Car Loan EMI <br />
              in <span className="text-blue-600">Seconds</span>
            </h2>
            <p className="text-gray-700 text-lg">
              Use our fast and free calculator to estimate your EMI — no paperwork, no waiting.
            </p>
            <Link
              to="/financial-tools-calculators/car-loan-emi-calculator"
              className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-lg rounded-full shadow-md transition"
            >
              Start Calculating <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="space-y-4 text-left">
            {features.map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <span className="text-xl" role="img" aria-label={item.text}>{item.icon}</span>
                <span className="text-gray-800 font-medium">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      <div className="container mx-auto p-6 mt-6">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
          Sample EMI Calculation for Popular Tata Cars
        </h2>
        <p className="text-gray-600 text-sm md:text-base mt-2 text-center">
          Use TheFinMaster’s Car Loan EMI Calculator for exact numbers based on your loan amount and interest rate.
        </p>
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="block md:hidden">
            {[
              { model: "Tata Tiago", price: "₹6 Lakh", loan: "₹5.4 Lakh", emi: "₹11,100" },
              { model: "Tata Punch", price: "₹8.5 Lakh", loan: "₹7.5 Lakh", emi: "₹15,300" },
              { model: "Tata Altroz", price: "₹9 Lakh", loan: "₹8 Lakh", emi: "₹16,300" },
              { model: "Tata Nexon", price: "₹12 Lakh", loan: "₹10.5 Lakh", emi: "₹21,600" },
              { model: "Tata Harrier", price: "₹17 Lakh", loan: "₹15 Lakh", emi: "₹30,900" },
            ].map((item, index) => (
              <div key={index} className="border mb-4 p-4 rounded-lg bg-white shadow-md">
                <h3 className="font-semibold text-gray-800 mb-2">{item.model}</h3>
                <div className="text-gray-600 mb-1"><strong>On-Road Price:</strong> {item.price}</div>
                <div className="text-gray-600 mb-1"><strong>Loan Amount:</strong> {item.loan}</div>
                <div className="text-gray-600"><strong>EMI (5 Years @10%):</strong> {item.emi}</div>
              </div>
            ))}
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full border-collapse border border-gray-300">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="p-3 border border-gray-300 text-left">Model</th>
                  <th className="p-3 border border-gray-300 text-left">On-Road Price (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">Loan Amount (₹)</th>
                  <th className="p-3 border border-gray-300 text-left">EMI (5 Years @10%)</th>
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {[
                  ["Tata Tiago", "₹6 Lakh", "₹5.4 Lakh", "₹11,100"],
                  ["Tata Punch", "₹8.5 Lakh", "₹7.5 Lakh", "₹15,300"],
                  ["Tata Altroz", "₹9 Lakh", "₹8 Lakh", "₹16,300"],
                  ["Tata Nexon", "₹12 Lakh", "₹10.5 Lakh", "₹21,600"],
                  ["Tata Harrier", "₹17 Lakh", "₹15 Lakh", "₹30,900"],
                ].map(([model, price, loan, emi], index) => (
                  <tr key={index} className="border border-gray-300">
                    <td className="p-3 border border-gray-300">{model}</td>
                    <td className="p-3 border border-gray-300">{price}</td>
                    <td className="p-3 border border-gray-300">{loan}</td>
                    <td className="p-3 border border-gray-300">{emi}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="w-full flex justify-center items-center my-6 px-4">
        <div className="w-full max-w-[984px]">
          <AdBanner displayOrder="3" />
        </div>
      </div>
      <div className="w-full px-2 py-6 bg-gradient-to-br from-sky-50 via-white to-sky-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-6xl mx-auto grid gap-10">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-lg p-6 md:p-10 border border-sky-200 dark:border-gray-700 hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-sky-600" />
              <span className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
                Documents Required for Tata Car Loan
              </span>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Salaried Individuals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300 mb-6">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN Card & Aadhaar Card</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Latest 3–6 months’ salary slips</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Form 16 or most recent ITR</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>6 months’ bank statements</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Address proof (utility bill, passport, rental agreement, etc.)</span>
              </div>
            </div>
            <h3 className="text-xl md:text-xl font-bold text-gray-800 dark:text-white mb-6">
              For Self-Employed Individuals:
            </h3>
            <div className="grid sm:grid-cols-2 gap-6 text-gray-700 dark:text-gray-300">
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>PAN & Aadhaar</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Business registration certificate or GST</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>Last 2–3 years’ ITR and financial statements</span>
              </div>
              <div className="p-5 bg-sky-50 dark:bg-gray-800 rounded-xl">
                <span>6 months’ bank statements</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-4xl mx-auto py-6 px-2">
        <h2 className="text-2xl md:text-3xl font-semibold text-center mb-8">
          Frequently Asked Questions
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="border rounded-lg overflow-hidden shadow-md"
            >
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                aria-expanded={activeIndex === index}
                aria-controls={`faq-answer-${index}`}
              >
                {faq.question}
                <Icon id={index} open={activeIndex} />
              </button>
              {activeIndex === index && (
                <div id={`faq-answer-${index}`} className="p-4 bg-gray-100">
                  <p className="text-gray-700">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      <section className="relative py-20 px-4 sm:px-8 bg-gradient-to-br from-white to-gray-100 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none z-0">
          <div className="absolute top-[-20%] left-[-10%] w-96 h-96 bg-indigo-300 opacity-30 blur-3xl rounded-full"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-purple-300 opacity-30 blur-3xl rounded-full"></div>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="relative z-10 max-w-4xl mx-auto bg-white/90 backdrop-blur-sm border border-gray-200 shadow-xl rounded-2xl p-10"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <div className="text-indigo-600 text-5xl">
              <BiCar />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed mb-4">
                Owning a Tata car is now easier with competitive and flexible car loan options from leading lenders. Whether you’re eyeing the value-packed Tiago or the premium Safari, a Tata Motors car loan gives you the freedom to own your dream vehicle on your terms.
              </p>
              <p className="text-gray-700 text-lg leading-relaxed">
                Use TheFinMaster to explore the best car loan offers, calculate your EMIs, and apply online with ease.
              </p>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
};

export default TataMotorsCarLoan;